import { Column, Columns } from "@r8er/components/marketing/columns";
import columnClasses from "@r8er/components/marketing/columns/columns.module.css";
import { Container } from "@r8er/components/marketing/container";
import { Content } from "@r8er/components/marketing/content";
import { Cta } from "@r8er/components/marketing/cta";
import { BookDemoButton } from "@r8er/components/marketing/ctas";
import { Divider } from "@r8er/components/marketing/divider";
import { Hero } from "@r8er/components/marketing/hero";
import { Layout } from "@r8er/components/marketing/layout";
import { Newsletter } from "@r8er/components/marketing/newsletter";
import { OurPartners } from "@r8er/components/marketing/our-members";
import { TeamBlockSimple } from "@r8er/components/marketing/team";
import { getAllR8ERs } from "@r8er/lib/cms";
import { withMiddleware } from "@r8er/lib/middleware";
import { InferGetServerSidePropsType } from "next";
import Head from "next/head";

const Watermark = () => (
  <svg
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      transform: "translate(-25%,-40%)",
      opacity: "50%",
    }}
    xmlns="http://www.w3.org/2000/svg"
    width="649.6"
    height="1051.7"
  >
    <path
      fill="#f0f0f0"
      d="M516 79a270 270 0 00-382 0 270 270 0 00-23 356l49-48c-56-79-49-190 22-260a203 203 0 01286 0c79 79 79 207 0 286L325 556l-74-73-48 47 122 122 191-191a270 270 0 000-382"
    />
    <path
      fill="#f0f0f0"
      d="M555 497l-15-14-48 47 15 15a258 258 0 010 364 258 258 0 01-364 0 258 258 0 010-364l182-182 74 74 48-48-122-122L95 497a325 325 0 000 460 325 325 0 00460 0 325 325 0 000-460"
    />
  </svg>
);

export const getServerSideProps = withMiddleware(
  async ({ req, prisma }) => {
    const r8ers = await getAllR8ERs();

    return {
      props: {
        r8ers,
      },
    };
  },
  { mode: "any" }
);

export default function About({
  r8ers,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  return (
    <div>
      <Head>
        <title>About Us - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <Hero isSlim title="About us" img="/about-hero.png" />

        <div style={{ position: "relative", overflow: "hidden" }}>
          <Watermark />

          <Container width={1100}>
            <Columns
              count={2}
              style={{
                padding: "var(--section-padding-normal)",
                paddingBottom: 0,
              }}
            >
              <Column>
                <h2
                  className="h2--standard"
                  style={{
                    fontSize: "calc(2.35 * var(--font-size-base))",
                    letterSpacing: "-1px",
                  }}
                >
                  We are the world's first, professionally vetted, digital
                  evaluation platform for disruptive healthcare that connects
                  technology with investment.
                </h2>

                <Columns
                  count={2}
                  classList={[columnClasses.marketing__columns_matchmobile]}
                >
                  <Column>
                    <TeamBlockSimple
                      name="Dr. Milla Marinova"
                      title="Founder & CEO"
                      imageUrl="/about-milla.png"
                    />
                  </Column>
                  <Column>
                    <TeamBlockSimple
                      name="Mr. Pavel Marinov"
                      title="COO"
                      imageUrl="/about-pavel.png"
                    />
                  </Column>
                </Columns>
              </Column>
              <Column>
                <p className="p--standard">
                  The story of R8ER begins with Dr. Milla Marinova, who after
                  completing her medical and biotechnology degrees at
                  Universities in London and Cambridge realized that many of her
                  former peers were struggling to get their great ideas, either
                  to a format that would be clear for investors to easily
                  understand, or to get a foot in the door in terms of financing
                  their projects.
                </p>
                <p className="p--standard">
                  Together with her brother, Pavel Marinov who had already
                  gained experience in both the financial and IT sectors, they
                  formed an idea that would not only solve this problem, but
                  furthermore act as an accelerator for cutting-edge, disruptive
                  tech ideas - and so R8ER was born...
                </p>
                <p className="p--standard">
                  After sharing their idea with some of Britain's top medical
                  and financial experts, a shortlist of experts slowly emerged
                  who have now become key members of R8ER's assessment team.
                </p>
              </Column>
            </Columns>
          </Container>
        </div>
        <Container>
          <Divider />
        </Container>

        <Content backgroundImage="/about-start.png">
          <h2 className="h2--standard">Where do we start?</h2>
          <p className="p--standard">
            R8ER utilizes a specialist assessment tool, in conjunction with our
            group of assessors, known as R8ERs, to evaluate entrepreneurial
            ideas submitted via the platform.
          </p>
        </Content>

        <OurPartners items={r8ers}>
          <h2 className="h2--standard">Who are the R8ERs?</h2>
          <BookDemoButton
            text="Become a R8ER"
            href="https://calendly.com/d/48r-82t-f2d/r8er-assessor-one-to-one-meeting"
          />
          <Columns count={2}>
            <Column>
              <p className="p--standard">
                R8ERs are a hand-selected group of top healthcare professionals
                who include partner-level investors, specialty professors,
                consultant clinicians and experts across regulatory, policy,
                financial and commercialization strategy. Here are some of our
                R8ERs:
              </p>
            </Column>
            <Column>
              <p className="p--standard">
                Ten R8ERs assess each company, focusing on the key areas of
                business model and strategy, product, science and technology,
                the team and advisors, financials and customers, commercial
                appeal and pitch technique.
              </p>
            </Column>
          </Columns>
        </OurPartners>

        <Content backgroundImage="/about-next.png">
          <h2 className="h2--standard">What happens next?</h2>
          <p className="p--standard">
            Once a company has received its scores and feedback from the
            assessors, its results are incorporated into the R8ER Reports, which
            are subsequently sent to a list of qualified investors for review.
          </p>
          <p className="p--standard">
            Should an investor be interested, they are able to contact the
            company directly to ask further questions and or negotiate to invest
            straight away. No complicated introductions, no laborious paperwork,
            just connect and go.
          </p>
        </Content>

        <Cta
          title="Start your R8ER Journey Today"
          description="If you are are looking for investment or looking to invest, sign up to R8ER today."
          buttonText="Get Started"
          buttonHref="/auth/signup"
          isBlue
        />

        <Newsletter />
      </Layout>
    </div>
  );
}
